import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { LogIn, AlertCircle, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

// Microsoft Logo as inline SVG
const MicrosoftLogo = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(true);
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for SSO token or error in URL
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    const ssoError = searchParams.get('sso_error');
    const errorDesc = searchParams.get('error_description');

    if (ssoToken) {
      // Handle SSO login
      handleSSOToken(ssoToken);
      // Clear URL params
      setSearchParams({});
    } else if (ssoError) {
      // Handle SSO error
      let errorMessage = 'SSO-Anmeldung fehlgeschlagen';
      if (ssoError === 'invalid_state') {
        errorMessage = 'Ungültige Sitzung. Bitte versuchen Sie es erneut.';
      } else if (ssoError === 'session_expired') {
        errorMessage = 'Sitzung abgelaufen. Bitte versuchen Sie es erneut.';
      } else if (ssoError === 'token_exchange_failed') {
        errorMessage = 'Authentifizierung fehlgeschlagen. Bitte kontaktieren Sie den Administrator.';
      } else if (errorDesc) {
        errorMessage = decodeURIComponent(errorDesc);
      }
      setError(errorMessage);
      toast.error(errorMessage);
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Check SSO status
  useEffect(() => {
    const checkSSO = async () => {
      try {
        const status = await authService.getSSOStatus();
        setSsoEnabled(status.enabled);
      } catch (err) {
        console.log('SSO status check failed:', err);
        setSsoEnabled(false);
      } finally {
        setSsoLoading(false);
      }
    };
    checkSSO();
  }, []);

  const handleSSOToken = async (token) => {
    setLoading(true);
    try {
      // Store token and fetch user info
      await loginWithToken(token);
      toast.success('Erfolgreich mit Microsoft angemeldet!');
      navigate('/dashboard');
    } catch (err) {
      setError('SSO-Anmeldung fehlgeschlagen');
      toast.error('SSO-Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Erfolgreich angemeldet!');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = () => {
    // Redirect to backend SSO endpoint
    window.location.href = authService.getSSOLoginUrl();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md px-4"
      >
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            Glasmatic Customer Self Care Portal
          </h1>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Melden Sie sich an, um fortzufahren</p>
        </div>

        <Card className="bg-white shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Anmelden</CardTitle>
            <CardDescription>Geben Sie Ihre Anmeldedaten ein</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* SSO Button */}
            {!ssoLoading && ssoEnabled && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mb-4 border-slate-300 hover:bg-slate-50"
                  onClick={handleSSOLogin}
                  disabled={loading}
                  data-testid="sso-login-button"
                >
                  <MicrosoftLogo />
                  Mit Microsoft anmelden
                </Button>
                
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">oder</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="border-slate-200"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Passwort</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-blue-600 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Passwort vergessen?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="border-slate-200"
                />
              </div>

              <Button
                type="submit"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-on-primary)'
                }}
                className="w-full hover:opacity-90"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Anmelden...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Anmelden
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-slate-500">
          <p>&copy; 2025 Glasmatic. Alle Rechte vorbehalten.</p>
        </div>
      </motion.div>
    </div>
  );
};
